import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ProductCardDto } from '../lib/api';
import { useStore } from '../lib/store';
import { ProductGrid } from '../components/ProductCard';
import { ButtonLink, ProductGridSkeleton } from '../components/ui';
import { usePageMeta } from '../lib/meta';

export function HomePage() {
  const { data } = useStore();
  const [newIn, setNewIn] = useState<ProductCardDto[] | null>(null);
  const [onSale, setOnSale] = useState<ProductCardDto[] | null>(null);

  usePageMeta({
    title: `${data?.store?.name ?? "Gill's Handmade"} — ${data?.store?.tagline ?? 'Handcrafted Jewellery'}`,
    description: data?.store?.description ?? '',
  });

  useEffect(() => {
    // Both strips are above the fold on a wide screen, so fetch them together
    // rather than waterfalling one after the other.
    void Promise.all([
      api.products({ category: 'new-in', pageSize: 8, sort: 'newest' }),
      api.products({ onSale: true, pageSize: 4 }),
    ])
      .then(([newest, sale]) => {
        setNewIn(newest.products);
        setOnSale(sale.products);
      })
      .catch(() => {
        setNewIn([]);
        setOnSale([]);
      });
  }, []);

  const featured = (data?.categories ?? []).filter(
    (c) => ['flowers-blossoms', 'summer-colours', 'czech-glass-earrings', 'necklaces'].includes(c.slug),
  );

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-border bg-surface">
        <div className="page-container flex flex-col items-center gap-6 py-20 text-center md:py-28">
          <p className="text-xs uppercase tracking-[0.3em] text-muted">
            {data?.store?.tagline ?? 'Handcrafted Jewellery'}
          </p>
          <h1 className="max-w-3xl text-4xl leading-tight md:text-6xl">
            Jewellery made by hand, one piece at a time
          </h1>
          <p className="max-w-xl text-sm leading-relaxed text-muted md:text-base">
            {data?.store?.description ??
              'Artisan jewellery handmade with ceramic pieces and Czech glass beads, inspired by flowers, leaves and the natural world.'}
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <ButtonLink to="/collections/all-jewellery" size="lg">
              Shop all jewellery
            </ButtonLink>
            <ButtonLink to="/collections/new-in" size="lg" variant="secondary">
              New arrivals
            </ButtonLink>
          </div>
        </div>
      </section>

      {/* Active sale banner, driven by whatever automatic rules are switched on */}
      {data?.activeSales && data.activeSales.length > 0 && (
        <section className="bg-sale py-3 text-center text-white">
          <div className="page-container">
            <p className="text-xs uppercase tracking-widest">
              {data.activeSales.map((s) => s.title).join(' · ')}
              {data.activeSales[0]?.description && (
                <span className="ml-2 opacity-80">{data.activeSales[0].description}</span>
              )}
            </p>
          </div>
        </section>
      )}

      {/* New arrivals */}
      <section className="page-container py-16">
        <div className="mb-8 flex items-end justify-between border-b border-border pb-4">
          <h2 className="text-2xl md:text-3xl">New arrivals</h2>
          <Link
            to="/collections/new-in"
            className="text-xs uppercase tracking-widest text-link hover:text-ink"
          >
            View all →
          </Link>
        </div>
        {newIn === null ? (
          <ProductGridSkeleton count={8} />
        ) : (
          <ProductGrid products={newIn} eagerCount={4} />
        )}
      </section>

      {/* Collections */}
      {featured.length > 0 && (
        <section className="bg-surface py-16">
          <div className="page-container">
            <h2 className="mb-8 border-b border-border pb-4 text-2xl md:text-3xl">
              Shop by collection
            </h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {featured.map((category) => (
                <Link
                  key={category.id}
                  to={`/collections/${category.slug}`}
                  className="group flex flex-col justify-between border border-border bg-background p-6 transition-colors hover:border-ink"
                >
                  <h3 className="font-heading text-lg leading-snug">{category.title}</h3>
                  <p className="mt-6 text-xs uppercase tracking-widest text-muted group-hover:text-ink">
                    {category.productCount} pieces →
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* On sale */}
      {onSale && onSale.length > 0 && (
        <section className="page-container py-16">
          <div className="mb-8 flex items-end justify-between border-b border-border pb-4">
            <h2 className="text-2xl md:text-3xl">On sale</h2>
            <Link
              to="/collections/summer-sale"
              className="text-xs uppercase tracking-widest text-link hover:text-ink"
            >
              View all →
            </Link>
          </div>
          <ProductGrid products={onSale} eagerCount={0} />
        </section>
      )}

      {/* Reassurance */}
      <section className="border-t border-border py-14">
        <div className="page-container grid gap-8 text-center md:grid-cols-3">
          {[
            ['Free UK delivery', 'Royal Mail Tracked 24 on every UK order.'],
            ['Made by hand', 'Each piece is made individually — most are one of a kind.'],
            ['Carefully wrapped', 'Sent in a linen pouch or recycled kraft gift box.'],
          ].map(([title, body]) => (
            <div key={title}>
              <h3 className="font-heading text-base">{title}</h3>
              <p className="mt-2 text-sm text-muted">{body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
